import React, { useEffect, useState } from 'react';
import { Global, css } from '@emotion/react';
import useForceUpdateOnWindowResize from '@zola/zola-ui/src/hooks/useForceUpdateOnWindowResize';

// Types
import type { WPublicWeddingView } from '@zola/svc-web-api-ts-client';
import type { BaseLayoutProps } from 'components/publicWebsiteV2/pageLayouts/types';

// Components
import SiteHeader from 'components/publicWebsiteV2/common/Header';
import ModalRoot from 'components/common/ModalRoot';
import ToastsContainer from 'components/common/ToastsContainer';

// Util
import { ThemeContext, ThemeContextType } from 'components/publicWebsiteV2/context';
import {
  mapBackgroundToCSS,
  mapWeddingToComponents,
} from 'components/publicWebsiteV2/util/mappers';
import UnifiedNav from 'components/common/header/UnifiedNav';
import Metadata from 'components/publicWebsiteV2/common/Metadata';
import { mapWeddingAccountToWebsiteDateProp } from 'util/websiteDateUtil';

import fontAssets from 'cards/stylesheets/_fonts.styles';
import { MEDIA_QUERY } from '@zola/zola-ui/src/styles/emotion';
import { Container } from './PublicV2.styles';

export type PublicV2Props = BaseLayoutProps &
  Pick<ThemeContextType, 'pageDataContext'> & {
    className?: string;
    inPreview?: boolean;
  };

const PublicV2: React.FC<PublicV2Props> = ({
  wedding,
  inPreview,
  children,
  className,
  pageDataContext,
}) => {
  useForceUpdateOnWindowResize();
  const { title, nav_items, slug, city, state_province } = wedding || {};
  const websiteDate = wedding && mapWeddingAccountToWebsiteDateProp(wedding, undefined);

  const background = wedding?.public_theme_v2?.components?.GLOBAL?.background;

  const [context, setContext] = useState({
    components: mapWeddingToComponents(wedding),
    highlightedComponents: [],
    wedding,
    inPreview,
    pageDataContext,
  });
  const globalStyles = css`
    html {
      body {
        ${fontAssets}

        .modal-xl {
          width: 900px;
        }
        .modal-dialog {
          width: 900px;
          ${MEDIA_QUERY.TABLET} {
            width: 748px;
          }
          ${MEDIA_QUERY.MOBILE} {
            position: relative;
            width: auto;
            margin: 10px;
          }
        }

        .modal-close {
          color: #a8a8aa;
        }
        a {
          text-decoration: none;
        }
      }
    }
  `;
  useEffect(() => {
    setContext({
      ...context,
      wedding,
      components: mapWeddingToComponents(wedding),
      pageDataContext,
    });
  }, [wedding, pageDataContext]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Container id="public-v2" className={className} background={mapBackgroundToCSS(background)}>
      <Metadata wedding={wedding as WPublicWeddingView} />
      <Global styles={globalStyles} />
      {!inPreview && (
        <>
          <UnifiedNav isHidden /> {/* @ts-ignore */}
          <ModalRoot />
          <ToastsContainer />
        </>
      )}
      <ThemeContext.Provider value={context}>
        <SiteHeader
          title={title}
          navItems={nav_items}
          slug={slug}
          websiteDate={websiteDate}
          city={city}
          stateProvince={state_province}
        />
        {children}
      </ThemeContext.Provider>
    </Container>
  );
};

export default PublicV2;
